import { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@mui/material/Modal";
import { IoCloseOutline } from "react-icons/io5";
import { createNewRole } from "../../../../../../../services/settings.services";
import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";
import {
  groupState,
  hasChildRows,
  indeterminateRef,
  toggleChild,
  toggleGroup,
  visibleChildren,
} from "./rolePermissions";

const inputCls =
  "w-full h-10 px-3 border border-ui-border rounded-lg text-sm text-ui-text outline-none focus:border-brand focus:ring-1 focus:ring-brand-light transition-all";

const AddRole = ({ permisions, open, handleClose, setRoles, setSnack }) => {
  const { t } = useTranslation("settings");
  const [isLoading, setIsLoading] = useState(false);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [createRole, setCreateRole] = useState({ name: "", permissions: [] });

  const closeModal = () => {
    handleClose();
    setCreateRole({ name: "", permissions: [] });
    setIsAllSelected(false);
  };

  const handleGroupChange = (e, group) => {
    const on = e.target.checked;
    setCreateRole((prev) => ({
      ...prev,
      permissions: toggleGroup(prev.permissions, group, on),
    }));
  };

  const handleChildChange = (e, group, child) => {
    const on = e.target.checked;
    setCreateRole((prev) => ({
      ...prev,
      permissions: toggleChild(prev.permissions, group, child, on),
    }));
  };

  const selectAllPermissions = (flag) => {
    setIsAllSelected(flag);
    setCreateRole((prev) => ({
      ...prev,
      permissions: flag
        ? Array.from(new Set(permisions.flatMap((p) => [p.id, ...p.children.map((c) => c.id)])))
        : [],
    }));
  };

  const handleCreateRoles = async () => {
    setIsLoading(true);
    const saved = await createNewRole(createRole);
    setIsLoading(false);
    if (!saved.error) {
      setRoles((prev) => [
        {
          ...saved.data.result,
          permissions: saved.data.result.permissions.map((p) => p.id),
        },
        ...prev,
      ]);
      setSnack((s) => ({ ...s, open: true, text: t("roles.added"), severity: "success" }));
      closeModal();
    } else {
      setSnack((s) => ({
        ...s,
        open: true,
        text: saved?.error?.response?.data?.message ?? t("common:messages.somethingWentWrong"),
        severity: "error",
      }));
    }
  };

  return (
    <Modal open={open} onClose={closeModal}>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-ui-surface rounded-xl shadow-2xl w-[680px] max-w-[95vw] max-h-[90vh] flex flex-col outline-none">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-ui-surface-sunken">
          <h2 className="text-lg font-semibold text-ui-text m-0 leading-none">{t("roles.create")}</h2>
          <button onClick={closeModal} className="text-ui-text-subtle hover:text-ui-text-muted transition-colors">
            <IoCloseOutline size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-4 flex-1 flex flex-col gap-3">

          <div>
            <label className="block text-xs font-medium text-ui-text-muted mb-1">{t("roles.name")}</label>
            <input
              className={inputCls}
              placeholder={t("roles.name")}
              name="name"
              value={createRole.name}
              onChange={(e) => setCreateRole((prev) => ({ ...prev, name: e.target.value }))}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-medium text-ui-text-muted m-0">{t("roles.permissions")}</p>
              <button
                onClick={() => selectAllPermissions(!isAllSelected)}
                className="text-xs text-brand hover:text-brand-hover font-medium transition-colors"
              >
                {isAllSelected ? t("roles.unselectAll") : t("roles.selectAll")}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
              {permisions?.map((item) => {
                const { checked, indeterminate } = groupState(createRole.permissions, item);
                return (
                  <div key={item.id} className="border border-ui-border rounded-lg p-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="accent-brand"
                        checked={checked}
                        ref={indeterminateRef(indeterminate)}
                        onChange={(e) => handleGroupChange(e, item)}
                      />
                      <span className="text-sm font-medium text-ui-text">{item.description}</span>
                    </label>
                    {hasChildRows(item) && (
                      <div className="mt-2 ml-6 flex flex-col gap-1.5">
                        {visibleChildren(item).map((child) => (
                          <label key={child.id} className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              className="accent-brand mt-0.5"
                              checked={createRole.permissions.includes(child.id)}
                              onChange={(e) => handleChildChange(e, item, child)}
                            />
                            <span className="text-xs text-ui-text-muted">{child.description}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-3 border-t border-ui-surface-sunken">
          <div className="flex gap-2">
            <BoltButton label={t("common:actions.cancel")} variant="outline" size="md" onClick={closeModal} />
            <BoltButton label={t("roles.create")} variant="primary" size="md" loading={isLoading} onClick={handleCreateRoles} />
          </div>
        </div>

      </div>
    </Modal>
  );
};

export default AddRole;
